import {useState, useCallback, useEffect, useId, useRef, useMemo, KeyboardEvent, FormEvent} from 'react'
import {Search, SlidersHorizontal, X} from 'lucide-react'
import {cn} from '@/lib/utils'
import {useI18n} from '@/i18n'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useAuth} from '@/contexts/auth-context'
import {useAnnouncer} from '@/components/shared/Announcer'
import {FilterChip} from '@/components/shared/FilterChip'
import {FilterPanel} from '@/components/shared/FilterPanel'
import type {
    EndpointContractFilterDescriptor,
    SearchAndFilterContract,
} from '@/types/endpoint-contract'
import {IS_NULL_SENTINEL} from '@/types/endpoint-contract'
import {unpackEntityLinkDisplayName} from '@/lib/entity-link-packing'
import type {SearchAndFilters} from '@/hooks/use-server-pagination'
import type {AttributeDefinition} from '@/types/attribute-definition'

// Tailwind class names — treated as style tokens, not user-visible strings.
const CHIP_WIDTH_DESKTOP = 'max-w-[240px]'
const CHIP_WIDTH_NARROW = 'max-w-[160px]'

// Discriminator type-table identifiers per attribute entity type. Used by
// the dynamic-attribute panel scoping rule (§4.10.1, issue 342.14 finding E)
// to find the contract filter that narrows by entity type. These are
// database table identifiers, not user-visible labels.
 
const DISCRIMINATOR_TYPE_TABLE: Record<string, string> = {
    identity: 'identity_types',
    party: 'party_types',
    resource: 'resource_types',
    functionGroup: 'function_group_types',
}
 

export interface UnifiedSearchBarProps {
    /**
     * The search/filter contract — declares which sort paths and filter
     * descriptors the bar surfaces. Server-paginated callers pass an
     * {@link EndpointContract} (a strict subtype) sourced from
     * {@code endpoint-contracts.ts}; in-memory callers pass a plain
     * {@link SearchAndFilterContract} literal that is not bound to a
     * backend endpoint.
     */
    contract: SearchAndFilterContract
    /**
     * Current search + filter state. Server-paginated callers feed the value
     * from {@link useServerPagination}; in-memory callers manage it with a
     * plain {@code useState(EMPTY_SEARCH_AND_FILTERS)} and run the result
     * through their own predicate to narrow an already-loaded array.
     */
    value: SearchAndFilters
    /** Commit the panel + text changes to the URL. */
    onChange(next: Partial<SearchAndFilters>): void
    /**
     * Attribute-definition entity type used to pick which dynamic attributes
     * render as filter sections. When absent, dynamic attributes are skipped.
     */
    attributeEntityType?: 'identity' | 'party' | 'resource' | 'functionGroup'
    /** Placeholder for the free-text input. Already translated. */
    placeholder?: string
    /**
     * Namespace prefix applied to `data-testid` values — lets pages host
     * multiple bars without duplicate selectors (§5.7).
     */
    testIdNamespace?: string
    /** When true, bar occupies full width (default); otherwise 64px fixed. */
    fullWidth?: boolean
    /**
     * When provided, overrides the default viewport detection for mobile
     * rendering. Used by the demo harness and Storybook stories to force
     * specific widths.
     */
    forcedBreakpoint?: 'desktop' | 'mobile' | 'narrow'
    /**
     * Fired once after every successful submit / chip removal — the caller
     * can use this for toast "Filter applied" announcements (FE-32 /
     * aria-live polite).
     */
    onLastChangeAnnouncement?(message: string): void
    /**
     * Spec §4.8.3 audit-class minimum-q threshold. When set, sub-threshold
     * non-empty {@code q} submits are blocked client-side and the hint
     * channel ({@link onMinQHintChange}) fires so the page can render an
     * inline hint. Empty / cleared input always passes through (no hint).
     */
    qMinChars?: number
    /**
     * Fired with {@code true} when the current text input is non-empty but
     * shorter than {@link qMinChars}, {@code false} otherwise. The page
     * uses this to render the inline 3-character hint outside the bar.
     */
    onMinQHintChange?(showHint: boolean): void
    /**
     * Type codes pinned by the page (issue 680). When set, the dynamic-
     * attribute pool is scoped to definitions matching one of these codes
     * (or to universal defs with empty {@code typeCodes}) — regardless of
     * whether the discriminator filter is in the contract. Used by the
     * promoted by-type routes where the filter is hidden but the type is
     * still pinned server-side.
     */
    lockedDiscriminatorValues?: readonly string[]
}

/**
 * Unified Search Bar — replaces free-text + inline filter controls with a
 * single contract-driven surface. See docs/specs/unified-search-and-filter
 * §5 (Frontend Design) for the full spec.
 */
export function UnifiedSearchBar({
    contract,
    value,
    onChange,
    attributeEntityType,
    placeholder,
    testIdNamespace,
    fullWidth = true,
    forcedBreakpoint,
    onLastChangeAnnouncement,
    qMinChars,
    onMinQHintChange,
    lockedDiscriminatorValues,
}: UnifiedSearchBarProps) {
    const i18n = useI18n()
    const appConfig = useAppConfig()
    const auth = useAuth()
    const isAdmin = auth.user?.roles.includes('Admin') ?? false
    const {announce} = useAnnouncer()
    /* WHY: the bar already builds a "filter X applied / removed" message for
       every chip change via `onLastChangeAnnouncement`, but no page wires
       that callback to anything. Fire the announce ourselves so SR users
       hear the filter took effect — and the optional caller callback still
       runs for any additional handler. Issue 803. */
    const emitAnnouncement = useCallback((msg: string) => {
        announce(msg)
        if (onLastChangeAnnouncement) onLastChangeAnnouncement(msg)
    }, [announce, onLastChangeAnnouncement])

    const [textInput, setTextInput] = useState(value.q)
    const [panelOpen, setPanelOpen] = useState(false)
    const lastSyncedQ = useRef(value.q)
    const triggerRef = useRef<HTMLButtonElement>(null)

    // Keep local text state synced with external (URL) changes.
    useEffect(() => {
        if (value.q !== lastSyncedQ.current) {
            lastSyncedQ.current = value.q
            setTextInput(value.q)
        }
    }, [value.q])

    // ── Breakpoint detection ────────────────────────────────────────────────
    const [breakpoint, setBreakpoint] = useState<'desktop' | 'mobile' | 'narrow'>(() => {
        if (forcedBreakpoint) return forcedBreakpoint
        if (typeof window === 'undefined') return 'desktop'
        const w = window.innerWidth
        if (w < 480) return 'narrow'
        if (w < 640) return 'mobile'
        return 'desktop'
    })
    useEffect(() => {
        if (forcedBreakpoint) {
            setBreakpoint(forcedBreakpoint)
            return
        }
        if (typeof window === 'undefined') return
        const onResize = () => {
            const w = window.innerWidth
            setBreakpoint(w < 480 ? 'narrow' : w < 640 ? 'mobile' : 'desktop')
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [forcedBreakpoint])

    const isMobile = breakpoint !== 'desktop'
    const isNarrow = breakpoint === 'narrow'

    // ── Dynamic attribute definitions for this entity type ──────────────────
    // Spec §4.10.1 + 342.19: this returns the UNSCOPED pool. Discriminator
    // narrowing happens inside FilterPanel against the panel's draft state
    // so the user sees the list change as they toggle checkboxes — not only
    // after Apply. The chip row outside the panel still uses URL state.
    const dynamicAttributeDefs: AttributeDefinition[] = useMemo(() => {
        if (!attributeEntityType) return []
        const map: Record<string, AttributeDefinition[]> = {
            identity: appConfig.identityAttributeDefinitions,
            party: appConfig.partyAttributeDefinitions,
            resource: appConfig.resourceAttributeDefinitions,
            functionGroup: appConfig.functionGroupAttributeDefinitions,
        }
        return (map[attributeEntityType] ?? []).filter(d => d.filterable && d.active)
    }, [attributeEntityType, appConfig])

    // Identify the discriminator filter key (e.g. 'PARTY_TYPE') so the panel
    // can scope dynamic attrs from its draft. Looked up via the typeTable
    // convention (party_types / resource_types / …).
    const discriminatorFilterKey: string | undefined = useMemo(() => {
        if (!attributeEntityType) return undefined
        const expectedTypeTable = DISCRIMINATOR_TYPE_TABLE[attributeEntityType]
        const filterMap = contract.filter as Record<string, EndpointContractFilterDescriptor>
        const entry = Object.entries(filterMap).find(([, d]) => d.typeTable === expectedTypeTable)
        return entry?.[0]
    }, [attributeEntityType, contract.filter])

    // ── Chip derivation from current URL state ──────────────────────────────
    const chips = useMemo(() => {
        const out: Array<{
            key: string
            label: string
            value: string
            values: string[]
            mode: 'labeled' | 'selfEvident'
            onRemove: () => void
            testId?: string
        }> = []
        const filterMap = contract.filter as Record<string, EndpointContractFilterDescriptor>
        const makeRemovedMsg = (label: string) =>
            (i18n.unifiedSearch.announce.filterRemoved as (p: {label: string}) => string)({label})
        // Map raw enum codes through labelOf(typeValuesByName[typeTable], code)
        // so chips render localized labels while the URL keeps the raw code.
        // For uuid+lookup descriptors the URL value is the pipe-packed
        // `id|displayName|entityType|subType` (issue 543) — chips render the
        // displayName slice; the trailing parts only exist so the picker can
        // restore on URL refresh without an extra `findLinks` round-trip.
        const resolveDisplay = (descriptor: EndpointContractFilterDescriptor): ((code: string) => string) => {
            if (descriptor.kind === 'uuid' && descriptor.lookup) {
                return c => unpackEntityLinkDisplayName(c)
            }
            if (descriptor.kind === 'csv' && descriptor.element === 'uuid' && descriptor.lookup) {
                return c => unpackEntityLinkDisplayName(c)
            }
            if (!descriptor.typeTable) return c => c
            const values = appConfig.typeValuesByName[descriptor.typeTable]
            if (!values) return c => c
            return c => labelOf(values, c)
        }
        const yesLabel = i18n.unifiedSearch.panel.yes as string
        const noLabel = i18n.unifiedSearch.panel.no as string
        for (const [key, descriptor] of Object.entries(filterMap)) {
            if (descriptor.adminOnly && !isAdmin) continue
            const raw = value.filters[key]
            if (raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)) continue
            const values = Array.isArray(raw) ? raw : [raw]
            if (!descriptor.displayName) {
                throw new Error(
                    `Filter descriptor '${descriptor.name}' missing displayName — contract must declare one`,
                )
            }
            const label = descriptor.displayName
            // BOOLEAN chips: read the per-field true/false label when provided;
            // otherwise fall back to `{displayName}: Yes` / `{displayName}: No`.
            // The `selfEvident` mode drops the `{label}: ` prefix because the
            // selected state label is semantic on its own ("Active" vs
            // "Inactive" needs no "Active:" prefix).
            if (descriptor.kind === 'boolean' && descriptor.booleanLabels && (raw === 'true' || raw === 'false')) {
                const selected = raw === 'true' ? descriptor.booleanLabels.true : descriptor.booleanLabels.false
                out.push({
                    key,
                    label,
                    value: selected,
                    values,
                    mode: 'selfEvident',
                    testId: `chip-${key}`,
                    onRemove: () => {
                        const nextFilters = {...value.filters}
                        delete nextFilters[key]
                        onChange({filters: nextFilters})
                        emitAnnouncement(makeRemovedMsg(selected))
                    },
                })
                continue
            }
            if (descriptor.kind === 'boolean' && (raw === 'true' || raw === 'false')) {
                // Fallback: no per-field labels → `{displayName}: Yes` / `{displayName}: No`.
                out.push({
                    key,
                    label,
                    value: raw === 'true' ? yesLabel : noLabel,
                    values,
                    mode: 'labeled',
                    testId: `chip-${key}`,
                    onRemove: () => {
                        const nextFilters = {...value.filters}
                        delete nextFilters[key]
                        onChange({filters: nextFilters})
                        emitAnnouncement(makeRemovedMsg(label))
                    },
                })
                continue
            }
            const display = resolveDisplay(descriptor)
            out.push({
                key,
                label,
                value: formatChipValue(values, i18n.unifiedSearch.panel.notSet as string, display),
                values,
                mode: 'labeled',
                testId: `chip-${key}`,
                onRemove: () => {
                    const nextFilters = {...value.filters}
                    delete nextFilters[key]
                    onChange({filters: nextFilters})
                    emitAnnouncement(makeRemovedMsg(label))
                },
            })
        }
        for (const [attrName, raw] of Object.entries(value.dynamicAttributes)) {
            if (raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)) continue
            const def = dynamicAttributeDefs.find(d => d.name === attrName)
            const label = def?.displayName ?? attrName
            const values = Array.isArray(raw) ? raw : [raw]
            // AttributeDefinition carries its own code→label pairs via
            // resolvedAllowedValues (see §4.10.3). When present, render the
            // label; the URL continues to carry the code.
            const codeToLabel: Record<string, string> = {}
            if (def?.resolvedAllowedValues) {
                for (const v of def.resolvedAllowedValues) codeToLabel[v.code] = v.label
            }
            const display = (c: string): string => codeToLabel[c] ?? c
            out.push({
                key: `attr-${attrName}`,
                label,
                value: formatChipValue(values, i18n.unifiedSearch.panel.notSet as string, display),
                values,
                mode: 'labeled',
                testId: `chip-attr-${attrName}`,
                onRemove: () => {
                    const nextAttrs = {...value.dynamicAttributes}
                    delete nextAttrs[attrName]
                    onChange({dynamicAttributes: nextAttrs})
                    emitAnnouncement(makeRemovedMsg(label))
                },
            })
        }
        return out
    }, [contract, value, dynamicAttributeDefs, isAdmin, onChange, emitAnnouncement, i18n, appConfig.typeValuesByName])

    const hasActiveFilters = chips.length > 0

    // WHY: min-q hint plumbing (audit-class spec §4.8.3) — fire
    // onMinQHintChange whenever the trimmed input crosses the qMinChars
    // boundary. Empty input always passes through (no hint, valid "no q").
    const trimmedTextInput = textInput.trim()
    const showMinQHint = qMinChars !== undefined
        && trimmedTextInput.length > 0
        && trimmedTextInput.length < qMinChars
    useEffect(() => {
        onMinQHintChange?.(showMinQHint)
    }, [showMinQHint, onMinQHintChange])

    // ── Event handlers ──────────────────────────────────────────────────────
    const submit = useCallback(() => {
        // WHY: spec §4.8.3 — audit-class endpoints reject sub-threshold q
        // server-side with a 400. Block the commit client-side too so the
        // page can show the inline hint without the user paying for a
        // round-trip + error toast. Empty q still commits (clears search).
        if (showMinQHint) {
            return
        }
        if (textInput !== value.q) {
            onChange({q: textInput})
            const msg = textInput
                ? (i18n.unifiedSearch.announce.search as (p: {query: string}) => string)({query: textInput})
                : (i18n.unifiedSearch.announce.searchCleared as string)
            emitAnnouncement(msg)
        }
    }, [textInput, value.q, onChange, emitAnnouncement, i18n, showMinQHint])

    const handleFormSubmit = useCallback((e: FormEvent) => {
        e.preventDefault()
        submit()
    }, [submit])

    const handleTextClear = useCallback(() => {
        setTextInput('')
        if (value.q) {
            onChange({q: ''})
            emitAnnouncement(i18n.unifiedSearch.announce.searchCleared as string)
        }
    }, [value.q, onChange, emitAnnouncement, i18n])

    const handleInputKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            setTextInput('')
        }
    }, [])

    const handleApply = useCallback((next: {filters: Record<string, string | string[]>; dynamicAttributes: Record<string, string | string[]>}) => {
        onChange({filters: next.filters, dynamicAttributes: next.dynamicAttributes})
        setPanelOpen(false)
        /* Build a summary of which filters are now active so the polite
           announcement names them ("Filters applied: Lifecycle state, Identity type")
           instead of the previous "Filter applied:" with empty placeholders. */
        const filterMap = contract.filter as Record<string, EndpointContractFilterDescriptor>
        const activeLabels: string[] = []
        for (const [key, val] of Object.entries(next.filters)) {
            const isActive = Array.isArray(val) ? val.length > 0 : !!val
            if (!isActive) continue
            const desc = filterMap[key]
            if (desc) activeLabels.push(desc.displayName)
        }
        for (const [defId, val] of Object.entries(next.dynamicAttributes)) {
            const isActive = Array.isArray(val) ? val.length > 0 : !!val
            if (!isActive) continue
            const def = dynamicAttributeDefs.find((d) => d.id === defId)
            if (def) activeLabels.push(def.displayName)
        }
        const summary = activeLabels.length > 0
            ? activeLabels.join(', ')
            : String(i18n.unifiedSearch.announce.noFiltersActive)
        emitAnnouncement(
            (i18n.unifiedSearch.announce.filterApplied as (p: {filters: string}) => string)({filters: summary}),
        )
        // Return focus to the trigger so keyboard users keep context.
        setTimeout(() => triggerRef.current?.focus(), 0)
    }, [onChange, emitAnnouncement, i18n, contract.filter, dynamicAttributeDefs])

    const handleClearAll = useCallback(() => {
        onChange({q: '', filters: {}, dynamicAttributes: {}})
        setTextInput('')
        setPanelOpen(false)
        emitAnnouncement(i18n.unifiedSearch.announce.filtersCleared as string)
        setTimeout(() => triggerRef.current?.focus(), 0)
    }, [onChange, emitAnnouncement, i18n])

    const handleClose = useCallback(() => {
        setPanelOpen(false)
        setTimeout(() => triggerRef.current?.focus(), 0)
    }, [])

    const chipMaxWidth = isNarrow ? CHIP_WIDTH_NARROW : CHIP_WIDTH_DESKTOP

    const testId = (suffix: string) => testIdNamespace ? `${testIdNamespace}-${suffix}` : suffix

    const resolvedPlaceholder = placeholder ?? (i18n.unifiedSearch.bar.placeholder as string)

    // Stable DOM id for the inline region so the trigger's aria-controls
    // resolves correctly. React's useId gives us an SSR-safe unique suffix.
    const panelId = useId()

    return (
        <div
            data-testid={testId('unified-search-bar')}
            className={cn(fullWidth ? 'w-full' : 'w-auto')}
        >
            <form
                role="search"
                onSubmit={handleFormSubmit}
                className={cn(
                    'flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1',
                    'focus-within:ring-1 focus-within:ring-ring',
                )}
            >
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true"/>
                {chips.map(c => (
                    <FilterChip
                        key={c.key}
                        label={c.label}
                        value={c.value}
                        values={c.values}
                        ariaLabel={(i18n.unifiedSearch.chip.removeLabel as (p: {name: string; values: string}) => string)(
                            c.mode === 'selfEvident'
                                ? {name: c.value, values: ''}
                                : {name: c.label, values: c.value},
                        )}
                        onRemove={c.onRemove}
                        maxWidthClass={chipMaxWidth}
                        mode={c.mode}
                        testId={testId(c.testId!)}
                    />
                ))}
                <input
                    type="search"
                    role="searchbox"
                    aria-label={resolvedPlaceholder}
                    data-testid={testId('input-search')}
                    placeholder={chips.length === 0 ? resolvedPlaceholder : undefined}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    className="min-w-[80px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
                {textInput && (
                    <button
                        type="button"
                        aria-label={i18n.unifiedSearch.bar.clearSearch as string}
                        data-testid={testId('btn-clear-search')}
                        onClick={handleTextClear}
                        className="rounded-full p-0.5 hover:bg-muted"
                    >
                        <X className="h-4 w-4 text-muted-foreground" aria-hidden="true"/>
                    </button>
                )}
                <button
                    ref={triggerRef}
                    type="button"
                    aria-label={i18n.unifiedSearch.bar.openFilters as string}
                    title={isMobile ? undefined : (i18n.unifiedSearch.bar.openFilters as string)}
                    aria-expanded={panelOpen}
                    aria-controls={panelId}
                    data-testid={testId('btn-filter-trigger')}
                    onClick={() => setPanelOpen(o => !o)}
                    className={cn(
                        'relative rounded-md p-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                >
                    <SlidersHorizontal className="h-4 w-4" aria-hidden="true"/>
                    {hasActiveFilters && (
                        <span
                            aria-hidden="true"
                            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary"
                        />
                    )}
                </button>
            </form>
            {/*
             * Panel renders inline below the bar at the same full-bar width
             * (§3.5 inline-collapse variant). It is a region, not a dialog —
             * Tab moves naturally out of it; Escape closes. There is no
             * internal scroll: when the panel is tall the page scrolls.
             */}
            {panelOpen && !appConfig.isLoading && (
                <div
                    id={panelId}
                    className="mt-2 rounded-md border border-input bg-background"
                >
                    <FilterPanel
                        contract={contract}
                        values={value.filters}
                        dynamicAttrValues={value.dynamicAttributes}
                        dynamicAttributeDefs={dynamicAttributeDefs}
                        isAdmin={isAdmin}
                        applyLabel={i18n.unifiedSearch.panel.apply as string}
                        clearAllLabel={i18n.unifiedSearch.panel.clearAll as string}
                        notSetLabel={i18n.unifiedSearch.panel.notSet as string}
                        anyLabel={i18n.unifiedSearch.panel.any as string}
                        yesLabel={i18n.unifiedSearch.panel.yes as string}
                        noLabel={i18n.unifiedSearch.panel.no as string}
                        fromLabel={i18n.unifiedSearch.panel.from as string}
                        toLabel={i18n.unifiedSearch.panel.to as string}
                        title={i18n.unifiedSearch.panel.title as string}
                        uuidPlaceholder={i18n.unifiedSearch.panel.uuidPlaceholder as string}
                        nativeDatePicker={isNarrow}
                        typeValuesByName={appConfig.typeValuesByName}
                        multiPickerInlineThreshold={appConfig.filterPickerInlineThreshold}
                        filterPickerSearchThreshold={appConfig.filterPickerSearchThreshold}
                        discriminatorFilterKey={discriminatorFilterKey}
                        lockedDiscriminatorValues={lockedDiscriminatorValues}
                        onApply={handleApply}
                        onClearAll={handleClearAll}
                        onClose={handleClose}
                        onValueToggle={({section, value, selected}) => {
                            const template = selected
                                ? i18n.unifiedSearch.announce.valueSelected as (p: {section: string; value: string}) => string
                                : i18n.unifiedSearch.announce.valueDeselected as (p: {section: string; value: string}) => string
                            emitAnnouncement(template({section, value}))
                        }}
                    />
                </div>
            )}
            {/*
             * aria-live region lives outside the bar DOM (visually hidden).
             * Caller wires messages via onLastChangeAnnouncement or the page
             * can render its own region based on fetch results — this keeps
             * the bar unaware of data loading state.
             */}
        </div>
    )
}

/**
 * Format chip value — joins list, truncates > 3 items with "... +N more".
 *
 * The caller supplies a `displayLabel` mapper so raw codes (`PERSON`) become
 * localized labels (`Person`, `Dienst`) for display while the URL stays on
 * the raw code. The mapper defaults to identity — safe for filters that
 * don't carry a type-table hint.
 *
 * The "Not set" sentinel always renders as the caller-provided label and
 * skips the display mapper (the sentinel is not a type code).
 */
export function formatChipValue(
    values: ReadonlyArray<string>,
    notSetLabel: string = 'Not set',
    displayLabel: (code: string) => string = c => c,
): string {
    const visible = values.map(v => v === IS_NULL_SENTINEL ? notSetLabel : displayLabel(v))
    if (visible.length === 0) return ''

    if (visible.length <= 3) return visible.join(', ')

    const shown = visible.slice(0, 3).join(', ')
    const extra = visible.length - 3
    // eslint-disable-next-line i18next/no-literal-string -- overflow template; caller localises message separately
    return `${shown} +${extra} more`
}
